import React, { useRef, useEffect, useMemo, useState } from 'react';
import { read, utils } from "xlsx";
import '../../styles/pages/addcsv.scss';
import UploadComponent from './UploadComponent';
import { useNavigate } from "react-router-dom";
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast'
import { Menu } from 'primereact/menu'
import { Dropdown } from 'primereact/dropdown';

import axios from "axios"

// PrimeReact imports
// import { Toast } from 'primereact/toast';
// import { Steps } from 'primereact/steps';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar"
 
function CreateSample(props) {

    const toast = useRef(null);
    const navigate = useNavigate();

    const [samples, setSamples] = useState([]);
    const [columns, setColumns] = useState([]);   
    const [loading, setLoading] = useState(false);
    const [preparedFiles, setPreparedFiles] = useState({});
    const [finalizeVisible, setFinalizeVisible] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [csvUploadError, setCsvUploadError] = useState({err: false, missing: [], nameErr: false});
    const [displayFiles, setDisplayFiles] = useState(false);
    const [clearConfirmVisible, setClearConfirmVisible] = useState(false);
    const [createSampleVisible, setCreateSampleVisible] = useState(false);
    const [createSampleInput, setCreateSampleInput] = useState({});
    const [addAttributeVisible, setAddAttributeVisible] = useState(false);
    const [pendingAttribute, setPendingAttribute] = useState('');
    const [fileType, setFileType] = useState(null);
    const templateMenu = useRef(null);

    const fileTypeOptions = ['pe_files', 'pacbio_files', 'fasta_files', 'se_files'];

    const dataTableLoaded = useRef(false);

    function getSessionStorageOrDefault(key, defaultValue) {
        const stored =  sessionStorage.getItem(key);
        if (!stored) {
          return defaultValue;
        }
        return JSON.parse(stored);
    
    }

    const [userid] = useState(
        getSessionStorageOrDefault('userid', null)
    );
    const groupIds = JSON.parse(sessionStorage.getItem('usergroups'));

    const getDataUserToken = JSON.parse(sessionStorage.getItem('apitoken'));
    
    const headers = {
        Authorization: `Bearer ${getDataUserToken}`,
    };

    useEffect(() => {
        document.title = 'Create Sample | Palmona Pathogenomics'
    }, []);

    const dataTableProps = useMemo(
        () => ({ sample: samples, columns: columns, loading: loading, dataTableLoaded: dataTableLoaded }), 
        [samples, columns, loading, dataTableLoaded] 
    );

    const expectedFiles = useMemo(() => {
        const val = []
        samples.forEach(sample => {
            switch (fileType) {
                case 'pe_files':
                    val.push(sample.pe_file1);
                    val.push(sample.pe_file2);
                    break;
                case 'pacbio_files':
                    val.push(sample.pcb_bam_file);
                    val.push(sample.pcb_pbi_file);
                    break;
                case 'fasta_files':
                    val.push(sample.fasta_file);
                    break;
                case 'se_files':
                    val.push(sample.se_file);
                    break;
                default:
                    break;
            }  
        });
        return val;
    }, [samples, fileType]);



    const unmatchedFiles = useMemo(() => 
        Object.keys(preparedFiles).filter(name => !expectedFiles.includes(name)),
        [preparedFiles, expectedFiles]
    )

    const fileTableProps = useMemo(
        () => Object.values(preparedFiles).map(file => {
            file.matched = expectedFiles.includes(file.name);
            return file
        }),
        [preparedFiles, expectedFiles]);

    const uploadReady = useMemo(() => {
        for (let i = 0; i < expectedFiles.length; i++) {
            if (!preparedFiles[expectedFiles[i]]) return false;
        }
        if (!fileType) return false;
        return true;
    }, [expectedFiles, preparedFiles, fileType]);

    const sampleFileMap = useMemo(() => {
        const map = {};
        samples.forEach(item => {
            const files = [];
            switch (fileType) {
                case 'pe_files':
                    files.push(item.pe_file1, item.pe_file2);
                    break;
                case 'pacbio_files':
                    files.push(item.pcb_bam_file, item.pcb_pbi_file);
                    break;
                case 'fasta_files':
                    files.push(item.fasta_file);
                    break;
                case 'se_files':
                    files.push(item.se_file);
                    break;
                default:
                    break;
            }
            map[item.sample_name] = files;
        })
        return map;
    }, [samples, fileType]);

    // match columns to file type
    useEffect(() => {
        const newColumns = [{field: 'sample_name', header: 'sample_name'}];
        switch (fileType) {
            case 'pe_files':
                newColumns.push({field: 'pe_file1', header: 'pe_file1'});
                newColumns.push({field: 'pe_file2', header: 'pe_file2'});
                break;
            case 'pacbio_files':
                newColumns.push({field: 'pcb_bam_file', header: 'pcb_bam_file'});
                newColumns.push({field: 'pcb_pbi_file', header: 'pcb_pbi_file'});
                break;
            case 'fasta_files':
                newColumns.push({field: 'fasta_file', header: 'fasta_file'});
                break;
            case 'se_files':
                newColumns.push({field: 'se_file', header: 'se_file'});
                break;
            default:
                break;
        }
        setColumns(newColumns);
    }, [fileType])

    // match create sample state to columns
    useEffect(() => {
        const newCreateSampleInput = {};
        columns.forEach(column => {
            if (newCreateSampleInput[column.field] === undefined) newCreateSampleInput[column.field] = ''
        });
        setCreateSampleInput(newCreateSampleInput);
    }, [columns]);

    // Download the template file
    const downloadTemplate = (templateName) => {

        const link = document.createElement('a');
        link.download = templateName;

        // 👇️ set to relative path
        link.href = '/resources/' + templateName;

        link.click();
    }

    const templateOptions = [
        {
            label: 'Paired End',
            command: () => downloadTemplate('p3_paired_end_sample_template.xlsx')
        },
        {
            label: 'Pacbio',
            command: () => downloadTemplate('p3_pacbio_sample_template.xlsx')
        },
        {
            label: 'Fasta',
            command: () => downloadTemplate('p3_fasta_sample_template.xlsx')
        },
        {
            label: 'Single End',
            command: () => downloadTemplate('p3_single_end_template.xlsx')
        }
    ]

    // This needs a sampleID, and this page allows creating multiple samples at once
    // So I can't go straight to Sample_Details from here
    // I need to go to a page that shows all the samples that were just created
    // const navigateToSampleDetails = (sample_name, sample_id) =>{ 
    //     // const path = "manage_sample/new_sample/upload-csv" ; 
    //     const path = "datasetsample_details" ; 
    //     navigate(path,{
    //         state:{
    //             id:dataset_id,
    //             name:dataset_name
    //         }
    //     })
    //   }    

    const readCSV = (files) => {
        setLoading(true)
        const [file] = files
        const fileReader = new FileReader();
        fileReader.onload = () => {
            const arrayBuffer = fileReader.result;
            const array = new Uint8Array(arrayBuffer);
            const binaryString = String.fromCharCode.apply(null, array)
            const wb = read(binaryString, {
                type: "binary"
            });
            const sheets = wb.SheetNames;
            if (sheets.length) {
                const sheetName = sheets[0];

                const rows = utils.sheet_to_json(wb.Sheets[sheets[0]], {
                    raw: true
                });

                dataTableLoaded.current = true;
                
                const merged_obj = Object.assign({}, ...rows);
                const keys = Object.keys(merged_obj);
                
                let data = keys.map((key) => {
                    let obj = { field: key, header: key };
                    return obj;
                });

                setLoading(false);

                const expected = ['sample_name'];
                switch (sheetName) {
                    case 'pe_files':
                        expected.push('pe_file1', 'pe_file2');
                        break;
                    case 'pacbio_files':
                        expected.push('pcb_bam_file', 'pcb_pbi_file');
                        break;
                    case 'fasta_files':
                        expected.push('fasta_file');
                        break;
                    case 'se_files':
                        expected.push('se_file');
                        break;
                    default:
                        return setCsvUploadError({
                            err: true,
                            missing: [],
                            nameErr: true
                        })
                }

                const missing = expected.filter(field => !keys.includes(field));
                if (missing.length) {
                    return setCsvUploadError({
                        err: true,
                        missing,
                        nameErr: false
                    });
                }

                setSamples(rows);
                setColumns(data);
                setFileType(sheetName);

            }
        };
        fileReader.readAsArrayBuffer(file);
        dataTableLoaded.current = true;
    };

    // const validateCSV = (csv_sample, keys) => {
    //     const unique_arr = checkUnique(csv_sample);

    //     const missing_arr = checkMissingFiles(csv_sample);
        
    //     const arr = [...unique_arr, ...missing_arr]
    // };

    // const [serverSamples, setServerSamples] = useState([]);

    // const [createsamples, setCreatesamples] = useState(false);

    // const checkMissingFiles = (csv_sample) => {
    //     const missing = []
    //     csv_sample.map((row) => {
    //         // Make sure the template has the following columns
    //         const exist1 = Object.keys(row).includes("File1");
    //         const exist2 = Object.keys(row).includes("File2");
            
    //         if(!exist1 || !exist2){
    //             missing.push(row.Samples);
    //         } 
 
    //         setServerSamples(serverSamples => [...serverSamples, row]);
    //         return 0;

    //     });

    //     return missing.toString() ;
    // };

    const checkUnique = (csv_sample) => {
        const obj = csv_sample.reduce((a, e) => {
            a[e.sample_name] = ++a[e.sample_name] || 0;
            return a;
        }, {});

        let names = Object.keys(obj);

        const unique_arr = names.filter((e) => {
            if (obj[e] > 0) {
                return e;
            } else {
                return null
            }

        }).map((filteredObj) => {
            return "Sample name " + filteredObj + " should be unique."
        });

        return unique_arr;
    };

    // const [isDone, setIsDone] = useState(false);
    // const [createdSampleIds, setCreatedSampleIds] = useState([]);


    // useEffect(() => {
    //     if (createsamples) {
    //         createSamplesAPI(serverSamples);
    //     }
    // }, [createsamples, serverSamples]);
    
    
    // const createSamplesAPI = async (local_samples) => {
    //     // Create the Sample objects on the server to post to the API

    //     // For now I'm using all of the group IDs that the user has access to
    //     // but we should probably only use the group ID of the "Organization" that
    //     // the user is currently in
    //     const getDataUserId    = JSON.parse(sessionStorage.getItem('userid'));
    //     const getDataUserToken = JSON.parse(sessionStorage.getItem('apitoken'));
    //     const groupIds         = JSON.parse(sessionStorage.getItem('usergroups'));

    //     const samplesArray = [];

    //     try {
    //         // Create the sample objects on the server
    //         local_samples.forEach(async (sample) => {

    //             const newSample = {
    //                 name: sample.Samples,
    //                 ownerId: getDataUserId,
    //                 groupId: groupIds,
    //                 sampleattributes: [],
    //             };

    //             const attributes = Object.keys(sample);

    //             for (const attribute of attributes){

    //                 // Skip the following attributes
    //                 if (attribute === "Samples"){
    //                     continue;
    //                 }
    //                 if (attribute === "File1"){
    //                     continue;
    //                 }
    //                 if (attribute === "File2"){
    //                     continue;
    //                 }

    //                 if (attribute === "id"){
    //                     continue;
    //                 }

    //                 newSample.sampleattributes.push({
    //                     name: attribute,
    //                     value: sample[attribute],
    //                 });
    //             }

    //             samplesArray.push(newSample);

    //         });

    //         const jsonObject = { json: samplesArray }
    //         const headers = {
    //             'Content-Type': 'application/json',
    //             Authorization: `Bearer ${getDataUserToken}`,
    //         };

    //         const create_sample_response = await axios.post( `${process.env.REACT_APP_WORKFLOW_API_PATH}wfapi/sample.bulk`, 
    //         jsonObject,
    //         {
    //             headers,
    //         })

    //         create_sample_response.data.forEach((response) => {
    //             const id = response.id;
    //             setCreatedSampleIds(createdSampleIds => [...createdSampleIds, id]);
    //         });

    //     } catch (error) {
    //         console.error(error);
    //     } finally {
    //         setIsDone(true);
    //     }

    //     setCreatesamples(false);
    // };

    // useEffect(() => {
    //     if (isDone) {
    //         const path = "/datasets/list_samples" ; 

    //         navigate(path,{
    //             state:{
    //                 sampleids:createdSampleIds
    //             }
    //         })
    //     }
    // }, [isDone, createdSampleIds, navigate]);

    const addAttribute = () => {
        setColumns(columns => [...columns, { field: pendingAttribute, header: pendingAttribute }]);
    }

    // const addSample = () => {

    //     const sample = prompt("Please enter the Sample Name:", "Sample");
    //     const row = { "Samples": sample };
        
    //     setSamples([...samples, row]);

    //     // Why is this seperate from the samples state?
    //     setServerSamples(serverSamples => [...serverSamples, row]);

    //     if(samples.length === 0){

    //         setColumns([ { field: "Samples", header: "Sample" } ]);
    //     }


    // }

    const cellEditor = (options) => {
        return textEditor(options);
    };

    const textEditor = (options) => {
        return <InputText type="text" value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />;
    };

    const columnTemplate = (item, props) => {
        // const isFile = props.field === 'File1' || props.field === 'File2';
        let isFile = false;
        
        switch (fileType) {
            case 'pe_files':
                if (props.field === 'pe_file1' || props.field === 'pe_file2') isFile = true;
                break;
            case 'pacbio_files':
                if (props.field === 'pcb_bam_file' || props.field === 'pcb_pbi_file') isFile = true;
                break;
            case 'fasta_files':
                if (props.field === 'fasta_file') isFile = true;
                break;
            case 'se_files':
                if (props.field === 'se_file') isFile = true;
                break;
            default:
                break;
        }
        const notPreparedClass = 'pi pi-exclamation-triangle text-danger';
        const preparedClass = 'pi pi-check-circle text-success';
        const fileIsPrepared = Boolean(preparedFiles[item[props.field]]);
        return (
            <div>
                {isFile && <i className={fileIsPrepared ? preparedClass : notPreparedClass}></i>}
                {item[props.field]}
            </div>
        )
    }

    const sampleDeleteTemplate = (item, props) => {
        return (
            <span className='m-3'>
                <Button 
                    icon="pi pi-times" 
                    rounded 
                    outlined 
                    severity="danger" 
                    aria-label="Delete" 
                    size='small' 
                    style={{height: '36px', width: '36px'}}
                    onClick={() => {
                        // if (samples.length) {
                            setSamples(samples => {
                                const newSamples = [...samples]
                                newSamples.splice(props.rowIndex, 1);
                                return newSamples;
                            })
                        // } else {
                        //     window.location.reload();
                        // }
                    }}
                />
            </span>
        )
    }

    const fileTemplate = (item, props) => {
        const fileMatch = expectedFiles.includes(item.name);
        const matchClass = 'pi pi-check-circle text-success'
        const notMatchClass = 'pi pi-exclamation-triangle text-warning'
        return (
            <div style={{display: 'flex', 'alignItems': 'center', height: '36px'}}>
                {!fileMatch && <Tooltip target={`.icon-${props.rowIndex}`} />}
                <i 
                    className={`icon-${props.rowIndex} ${fileMatch ? matchClass : notMatchClass}`}
                    data-pr-tooltip="File does not match any expected by sample"
                    data-pr-position="right"
                    data-pr-at="right+5 top"
                    data-pr-my="left center-2"
                ></i>
                {item[props.field]}
                <span className='m-3'>
                    <Button 
                        icon="pi pi-times" 
                        rounded 
                        outlined 
                        severity="danger" 
                        aria-label="Delete" 
                        size='small' 
                        style={{height: '36px', width: '36px'}}
                        onClick={() => setPreparedFiles(files => {
                            const newFiles = {...files}
                            delete newFiles[item.name];
                            return newFiles;
                        })}
                    />
                </span>
                
            </div>
        )
    }


    const onCellEditComplete = e => {
        let { rowData, newValue, field, originalEvent: event } = e;
        const newSamples = [...samples];
        newSamples[e.rowIndex] = e.newRowData;
        setSamples(newSamples);

        switch (field) {
            default:
                if (newValue.trim().length > 0) rowData[field] = newValue;
                else event.preventDefault();
                break;
        }
    };

    const addFile = files => {
        const newPreparedFiles = {...preparedFiles};
        let newUnmatchedCount = 0;
        for (let i = 0; i < files.length; i++) {
            newPreparedFiles[files[i].name] = files[i];
            if (!expectedFiles.includes(files[i].name)) newUnmatchedCount++;
        }
        setPreparedFiles(newPreparedFiles);
        if (newUnmatchedCount === 0) toast.current.show({severity:'success', summary: 'Success', detail:`Added ${files.length} file${files.length > 1 ? 's' : ''} for upload.`, life: 3000});
        else toast.current.show({severity:'warn', summary: 'Warning', detail:`Added ${files.length} file${files.length > 1 ? 's' : ''} for upload. ${newUnmatchedCount} added file${newUnmatchedCount > 1 ? 's' : ''} not found in sample.`, life: 3000});
    }

    // I want to use this to clear samples, but currently resetting the samples array crashes the page. Until I figure this out, the clear samples button will reload the page.
    const clearSample = () => {
        setSamples([]);
        setColumns([]);
    }

    const startSampleCreation = async () => {
        setLoadingProgress(loadingProgress + 1);
        const createdSamples = Promise.all(samples.map(sampleRow => {
            const form = new FormData();
            form.append('name', sampleRow.sample_name);
            form.append('ownerId', userid);
            form.append('groupId', groupIds);
            return fetch(process.env.REACT_APP_WORKFLOW_API_PATH + 'wfapi/sample', {
                method: 'POST',
                headers,
                body: form
            })
                .then(res => res.json());
        }));
        const attributeMap = {};
        const reservedNames = new Set();
        reservedNames.add('sample_name');
        switch (fileType) {
            case 'pe_files':
                reservedNames.add('pe_file1');
                reservedNames.add('pe_file2');
                break;
            case 'pacbio_files':
                reservedNames.add('pcb_bam_file');
                reservedNames.add('pcb_pbi_file');
                break;
            case 'fasta_files':
                reservedNames.add('fasta_file');
                break;
            case 'se_files':
                reservedNames.add('se_file');
                break;
            default:

        }
        samples.forEach(item => {
            attributeMap[item.sample_name] = {};
            Object.entries(item).filter(attr => !reservedNames.has(attr)).forEach(attr => {
                attributeMap[item.sample_name][attr[0]] = attr[1];
            })
        })
        Promise.all((await createdSamples).map(async sample => {
            // create sample attributes
            Object.entries(attributeMap[sample.name]).forEach(attr => {
                const attrForm = new FormData();
                attrForm.append('name', attr[0]);
                attrForm.append('value', attr[1]);
                attrForm.append('sampleId', sample.id);
                fetch(process.env.REACT_APP_WORKFLOW_API_PATH +  'wfapi/sample_attribute', {
                    method: 'POST',
                    headers,
                    body: attrForm
                });
            })
            // upload files for sample
            const uploadedFiles = Promise.all(sampleFileMap[sample.name].map(async (file, i) => {
                const form = new FormData();
                form.append('ownerId', userid);
                form.append('file', preparedFiles[file]);
                form.append('groupId', groupIds);
                const uploadedFile = await fetch(process.env.REACT_APP_WORKFLOW_API_PATH + 'wfapi/file', {
                    method: 'POST',
                    headers,
                    body: form
                }).then(res => {
                    setLoadingProgress(loadingProgress + 1);
                    return res.json()
                });
                const fileAttributes = ['type']
                if (fileType === 'pe_files') fileAttributes.push('direction');
                else if (fileType === 'pacbio_files') fileAttributes.push('pbfile');

                return Promise.all(
                    [
                        uploadedFile,
                        ...fileAttributes.map(attr => {
                            const attrForm = new FormData();
                            attrForm.append('name', attr);
                            if (attr === 'type') {
                                switch(fileType) {
                                    case 'pe_files':
                                        attrForm.append('value', 'paired_end');
                                        break;
                                    case 'pacbio_files':
                                        attrForm.append('value', 'pacbio');
                                        break;
                                    case 'fasta_files':
                                        attrForm.append('value', 'fasta');
                                        break;
                                    case 'single_end':
                                        attrForm.append('value', 'single_end');
                                        break;
                                    default:
                                        break;
                                }
                            } else if (attr === 'direction') {
                                if (i === 0) attrForm.append('value', 'forward');
                                else if (i === 1) attrForm.append('value', 'reverse');
                            } else if (attr === 'pbfile') {
                                if (i === 0) attrForm.append('value', 'bam');
                                else if (i === 1) attrForm.append('value', 'pbi');
                            }
                            attrForm.append('fileId', uploadedFile.id);
                            return fetch(process.env.REACT_APP_WORKFLOW_API_PATH + 'wfapi/file_attribute' , {
                                method: 'POST',
                                headers,
                                body: attrForm
                            }).then(res => res.json());
                        })
                    ]
                );
            }));
            // associate files to sample
            return Promise.all((await uploadedFiles).map(file => {
                const assocForm = new FormData();
                assocForm.append('fileId', file[0].id);
                return fetch(process.env.REACT_APP_WORKFLOW_API_PATH + 'wfapi/sample/' + sample.id, {
                    method: 'PATCH',
                    headers,
                    body: assocForm
                }).then(res => res.json());
            }))
        }))
            .then(async () => {
                navigate('/datasets/list_samples', {
                    state: {
                        sampleids: (await createdSamples).map(sample => sample.id)
                    }
                });
            })

    }

    return (
        <main className='add-csv mt-2'>
            <Toast ref={toast} />
            <div className=''>
                <div className='row'>
                    <div className='col-10'>
                        <h5>Create Samples : Click the "+ Sample" button to create a new Sample, or upload xlsx files.</h5>
                        <p>Please upload XLSX file of metadata for the sample you would like to add, according to the template.</p>
                    </div>
                    <div className='col-10'>
                        <p>After creating Samples, you will be able to add files, and attributes from the Sample Details page.</p>
                    </div>
                    <div className='col-2 p-0'>
                        <Menu model={templateOptions} popup ref={templateMenu}/>
                        <Button 
                            label="Sample" 
                            size="small" 
                            outlined 
                            className='p-button-sm p-button p-component p-button-outlined' 
                            icon="bi bi-plus-lg" 
                            iconPos="left" 
                            // disabled={samples.length === 0} 
                            onClick={() => setCreateSampleVisible(true)} 
                            // tooltip={samples.length ? 'Add Samples' : 'Add template before creating samples' }
                            // tooltipOptions={{ showOnDisabled: true, disabled:samples.length > 0, position: 'left' }}
                        />
                        <Button label="Download Template" size="small" outlined className='mt-3 download-button p-button-sm p-button p-component ' icon="bi bi-download" iconPos="left" onClick={e => templateMenu.current.toggle(e)} />
                    </div>
                </div>
                <div className='mt-1'>
                    {samples.length === 0 && <UploadComponent callBackFunction={readCSV} label={"Drag & Drop xlsx files here."} acceptedType={{ 'application/vnd.ms-excel': ['.xlsx', '.csv', '.xls'] }} />}
                    {samples.length > 0 && <UploadComponent callBackFunction={addFile} label={"Drag & Drop files here."} /*acceptedType={{ 'fastq': ['.fastq.gz'] }}*/ />}
                </div>
                <div className='mt-2'>
                    {samples.length > 0 && <div className="card">
                        <DataTable emptyMessage ={<></>}
                            value={displayFiles ? fileTableProps : []}
                            pt={{
                                row: {
                                    className: 'testclass',
                                    id: 'testid'
                                }
                            }}
                        >
                            <Column key='name' field='name' header={<p>Uploaded Files{unmatchedFiles.length > 0 && <span style={{color: 'red'}}> - {unmatchedFiles.length} Unmatched</span> }</p>} body={fileTemplate}/>
                            <Column header={() => <i className={`pi pi-angle-${displayFiles ? 'up' : 'down'}`} onClick={() => setDisplayFiles(!displayFiles)}></i>} style={{textAlign: 'justify', direction: 'rtl'}}/>
                        </DataTable>
                    </div>}
                    {samples.length > 0 &&
                        <div className='row'>
                            <div className='col'>
                                <InputText id="alphanumeric" keyfilter="alphanum" className="p-inputtext-sm" placeholder="Search" />

                            </div>
                            <div className='col text-right'>
                                <Button label="Attribute" size="small" outlined className='p-button-sm p-button p-component p-button-outlined mr-1' icon="bi bi-plus-lg" iconPos="left" onClick={() => setAddAttributeVisible(true)} />
                            </div>
                        </div>
                    }
                    {/* <DataTableComponent {...dataTableProps} /> */}
                    {samples.length > 0 && <div className="card">
                        <DataTable 
                            value={dataTableProps.sample} 
                            editMode="cell"
                            tableStyle={{ minWidth: '50rem' }}>
                            <Column key='delete' body={sampleDeleteTemplate} style={{ width: '10%' }}/>
                            {dataTableProps.columns.map((col, i) => {
                                return <Column key={col.field} field={col.field} header={col.header} 
                                    editor={(options) => cellEditor(options)} onCellEditComplete={onCellEditComplete} body={columnTemplate}
                                />
                            })}
                        </DataTable>
                    </div>}
                </div>
                {samples.length > 0 && <div className='row'>
                    <div className='col text-left'>
                        <Button label="Start Over" size="small" outlined className='p-button-sm p-button p-component p-button-outlined mr-1' icon="bi bi-arrow-repeat" iconPos="left" onClick={() => setClearConfirmVisible(true)}/>
                    </div>
                    <div className='col text-right'>
                        {/* <StepsNavigation {...StepperProps} /> */}
                        <Button 
                            label="Create Samples" 
                            disabled={!uploadReady} 
                            size="small" 
                            outlined 
                            className='p-button-sm p-button p-component p-button-outlined' 
                            icon="bi bi-arrow-right" 
                            iconPos="right" 
                            tooltip={uploadReady ? 'Submit Samples and Files' : 'Upload files before creating sample' }
                            tooltipOptions={{ showOnDisabled: true, disabled:uploadReady, position: 'left' }} 
                            onClick={() => setFinalizeVisible(true) } />
                    </div>
                </div>}
                
            </div>
            <Dialog header="Create Sample" visible={createSampleVisible} style={{ width: '30vw' }} onHide = {() => setCreateSampleVisible(false)}>
                <div>File Type: <Dropdown value={fileType} options={fileTypeOptions} onChange={e => setFileType(e.value)} placeholder='Select a file type' disabled={samples.length > 0}/></div>
                <br/>
                {Object.keys(createSampleInput).map(field => {
                    return (
                        <>
                            <InputText className='mb-2' placeholder={field === 'Samples' ? 'Sample Name (required)' : field} value={createSampleInput[field]} onChange={(e) => setCreateSampleInput(Object.assign({...createSampleInput}, {[field]: e.target.value}))}/>
                            <br />
                        </>
                    )
                })}
                <Button icon='pi pi-plus' label='Add Sample' disabled={createSampleInput.sample_name?.length === 0} onClick={() => {
                    const newSample = {};
                    const newCreateSampleInput = {};
                    Object.entries(createSampleInput).forEach(([field, value]) => {
                        if (value.length > 0) {
                            newSample[field] = value;
                            newCreateSampleInput[field] = '';
                        }
                    });
                    setSamples([...samples, newSample]);
                    setCreateSampleInput(newCreateSampleInput);
                    setCreateSampleVisible(false);
                }}/>
            </Dialog>
            <Dialog header="Add Attribute" visible={addAttributeVisible} style={{ width: '30vw' }} onHide={() => setAddAttributeVisible(false)}>
                <InputText className='mb-4' placeholder='Attribute Name' value={pendingAttribute} onChange={e => setPendingAttribute(e.target.value)}/>
                <br />
                <Button icon="pi pi-check" onClick={() => {addAttribute(); setAddAttributeVisible(false)}} label="Confirm" />
            </Dialog>
            <Dialog header="Warning!" visible={clearConfirmVisible} style={{ width: '50vw' }} onHide={() => setClearConfirmVisible(false)}>
                <p className="mb-3">
                    This will reset any work currently active on this page. Do you want to proceed?
                </p>
                <Button icon="pi pi-times" onClick={() => setClearConfirmVisible(false)} label="Cancel" className='mr-4'/>
                <Button icon="pi pi-check" onClick={() => window.location.reload()} label="Confirm" />
            </Dialog>
            <Dialog visible={csvUploadError.err} header={<div className="flex align-items-center">
                <span className="flex align-items-center justify-content-center bg-red-100 text-red-800 mr-3 border-circle" style={{ width: '32px', height: '32px' }}>
                    <i className="pi pi-exclamation-circle text-lg"></i>
                </span>
                <span className="font-medium text-2xl text-900">Spreadsheet Upload Error</span>
            </div>} onHide={() => setCsvUploadError({err: false, missing: [], nameErr: false})}>
                {!csvUploadError.nameErr ? <div className='flex'>
                    <p>Missing the following fields:</p>
                    <ul>
                        {csvUploadError.missing.map(field => 
                            <li>{field}</li>    
                        )}
                    </ul>
                </div> : 
                <div className='flex'>
                    <p>Sheet name must match one of the template options:</p>
                    <ul>
                        <li>pe_files</li>
                        <li>pacbio_files</li> 
                        <li>fasta_files</li> 
                        <li>se_files</li>
                    </ul>
                </div>}

            </Dialog>
            <Dialog visible={finalizeVisible} header={<div className="flex align-items-center">
                <span className="flex align-items-center justify-content-center bg-cyan-100 text-cyan-800 mr-3 border-circle" style={{ width: '32px', height: '32px' }}>
                    <i className="pi pi-info text-lg"></i>
                </span>
                <span className="font-medium text-2xl text-900">Confirm Sample Creation</span>
            </div>} onHide={() => setFinalizeVisible(false)} footer={<div className=" border-top-1 surface-border pt-3">
                {!loadingProgress && <Button icon="pi pi-times" onClick={() => setFinalizeVisible(false)} label="Cancel" className="p-button-text" />}
                {!loadingProgress && <Button icon="pi pi-check" onClick={() => {startSampleCreation()}} label="Submit" />}
            </div >} modal breakpoints={{ '960px': '75vw', '640px': '100vw' }} style={{ width: '60vw', height: '30vw' }}>
                {!loadingProgress && <div className="flex">
                    Press confirm to begin sample submission and file upload.
                </div>}
                {loadingProgress > 0 && <div className="flex">
                    Please remain on this page while samples are created and files are uploaded.
                </div>}
                {loadingProgress > 0 && <ProgressBar value={Math.floor((loadingProgress / (Object.keys(preparedFiles).length + 1)) * 100)}></ProgressBar>}
            </Dialog>
            {/* <SampleFileUpload visible={uploadVisible} setVisible={setUploadVisible}/> */}
        </main>
    );
}

export default CreateSample;